import { ChevronDown, ChevronUp, GripVertical, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { PANEL_TYPE_META } from '@/lib/panels'

import { BLOCK_TYPES, blockSummary, type Block, type BlockType } from '../config-types'

/**
 * The panel's blocks in order. Drag a block to reorder (HTML5 drag and drop, no
 * extra dependency); keyboard: Alt+↑ / Alt+↓ on a focused block, or the visible
 * Move up/down buttons. Focus follows the moved block. "+ Add block" opens the list
 * of block types.
 */
export function BlockList({
  blocks,
  selectedIndex,
  onSelect,
  onAdd,
  onReorder,
}: {
  blocks: Block[]
  selectedIndex: number
  onSelect: (i: number) => void
  onAdd: (type: BlockType) => void
  /** move the block at `from` to position `to` */
  onReorder: (from: number, to: number) => void
}) {
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)
  // after a keyboard/button move, focus goes to the block at its new position
  const [focusTo, setFocusTo] = useState<{ index: number; target: 'item' | 'up' | 'down' } | null>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const upRefs = useRef<(HTMLButtonElement | null)[]>([])
  const downRefs = useRef<(HTMLButtonElement | null)[]>([])
  useEffect(() => {
    if (!focusTo) return
    const refs = focusTo.target === 'up' ? upRefs : focusTo.target === 'down' ? downRefs : itemRefs
    // a move button disappears at the list edge — fall back to the block itself
    ;(refs.current[focusTo.index] ?? itemRefs.current[focusTo.index])?.focus()
    setFocusTo(null)
  }, [focusTo, blocks])

  const move = (from: number, to: number, target: 'item' | 'up' | 'down') => {
    if (to < 0 || to > blocks.length - 1) return
    onReorder(from, to)
    setFocusTo({ index: to, target })
  }

  return (
    <div>
      <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">
        Blocks · {blocks.length}
      </div>
      <ul aria-label="Blocks" className="space-y-1">
        {blocks.map((b, i) => {
          const selected = i === selectedIndex
          return (
            <li
              key={i}
              draggable
              onDragStart={(e) => { setDragFrom(i); e.dataTransfer.effectAllowed = 'move' }}
              onDragOver={(e) => { if (dragFrom !== null) { e.preventDefault(); setDragOver(i) } }}
              onDragLeave={() => setDragOver((v) => (v === i ? null : v))}
              onDrop={(e) => {
                e.preventDefault()
                if (dragFrom !== null && dragFrom !== i) onReorder(dragFrom, i)
                setDragFrom(null)
                setDragOver(null)
              }}
              onDragEnd={() => { setDragFrom(null); setDragOver(null) }}
              className={[
                'relative flex items-center gap-1.5 rounded-[5px] border px-1.5 py-1.5 transition-colors',
                selected ? 'border-line-strong bg-surface-selected' : 'border-transparent hover:bg-surface-raised',
                dragOver === i && dragFrom !== i ? 'border-primary' : '',
                selected ? 'before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:rounded-l-[5px] before:bg-primary' : '',
              ].join(' ')}
            >
              <GripVertical aria-hidden className="size-3.5 shrink-0 cursor-grab text-text-3" />
              <button
                type="button"
                ref={(el) => { itemRefs.current[i] = el }}
                onClick={() => onSelect(i)}
                onKeyDown={(e) => {
                  if (!e.altKey) return
                  if (e.key === 'ArrowUp' && i > 0) { e.preventDefault(); move(i, i - 1, 'item') }
                  if (e.key === 'ArrowDown' && i < blocks.length - 1) { e.preventDefault(); move(i, i + 1, 'item') }
                }}
                aria-current={selected ? 'true' : undefined}
                title="Drag, or Alt+↑ / Alt+↓, to reorder"
                className="flex min-w-0 flex-1 items-baseline gap-2 text-left text-[12.5px] focus-visible:-outline-offset-2"
              >
                <span className={selected ? 'shrink-0 text-foreground' : 'shrink-0 text-text-2'}>
                  {PANEL_TYPE_META[b.type]?.label ?? b.type}
                </span>
                <span className="ml-auto min-w-0 truncate text-[11.5px] text-text-3">{blockSummary(b)}</span>
              </button>
              <span className="flex shrink-0">
                <button
                  type="button"
                  ref={(el) => { upRefs.current[i] = el }}
                  onClick={() => move(i, i - 1, 'up')}
                  disabled={i === 0}
                  aria-label="Move block up"
                  title="Move up (Alt+↑)"
                  className="rounded p-0.5 text-text-3 transition-colors hover:bg-surface-active hover:text-foreground disabled:pointer-events-none disabled:text-line-strong"
                >
                  <ChevronUp aria-hidden className="size-3.5" />
                </button>
                <button
                  type="button"
                  ref={(el) => { downRefs.current[i] = el }}
                  onClick={() => move(i, i + 1, 'down')}
                  disabled={i === blocks.length - 1}
                  aria-label="Move block down"
                  title="Move down (Alt+↓)"
                  className="rounded p-0.5 text-text-3 transition-colors hover:bg-surface-active hover:text-foreground disabled:pointer-events-none disabled:text-line-strong"
                >
                  <ChevronDown aria-hidden className="size-3.5" />
                </button>
              </span>
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        onClick={() => setAdding((v) => !v)}
        aria-expanded={adding}
        className="mt-2.5 inline-flex w-full items-center justify-center gap-1 rounded-md border border-line-strong bg-surface-raised px-2.5 py-1 text-[12px] text-foreground transition-colors hover:bg-surface-active"
      >
        <Plus aria-hidden className="size-3.5" />
        Add block
      </button>
      {adding && (
        <ul aria-label="Block types" className="mt-1.5 space-y-px rounded-md border border-line-strong bg-popover p-1">
          {BLOCK_TYPES.map((t) => (
            <li key={t}>
              <button
                type="button"
                onClick={() => { onAdd(t); setAdding(false) }}
                className="w-full rounded px-2 py-1 text-left text-[12.5px] text-text-2 transition-colors hover:bg-surface-active hover:text-foreground"
              >
                {PANEL_TYPE_META[t].label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
